import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
    Box,
    Container,
    FormControl,
    Grid2,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    Input,
    CircularProgress,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DateRangeIcon from "@mui/icons-material/DateRange";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
// You might want specific icons for each category if WarningAmberIcon isn't always suitable
// import PeopleIcon from '@mui/icons-material/People'; // Example for Followers
// import WcIcon from '@mui/icons-material/Wc'; // Example for Gender
// import CakeIcon from '@mui/icons-material/Cake'; // Example for Age
// import LocationCityIcon from '@mui/icons-material/LocationCity'; // Example for City
import {
    AgeDemographics,
    CityDemographics,
    Followers,
    GenderDemographics,
    Interactions,
    Views,
} from "./components"; // Assuming these are correctly imported from your project structure
import useAuth from "../../../context"; // Assuming this is correctly imported
import API_ENDPOINTS from "../../../apis/endpoints"; // Assuming this is correctly imported

const dateInputStyle = {
    width: 220,
    border: "1px solid #ccc",
    borderRadius: 1,
    px: 1,
    py: 0.5,
    "&:hover": { borderColor: "primary.main" },
    "&:before, &:after": { display: "none" },
};

const today = new Date().toISOString().split("T")[0];
const blueThemeColor = "#399BFF";

// **Reusable Data Unavailable Message Component**
const DataUnavailableMessage = ({
    icon: IconComponent,
    title,
    message,
    suggestion,
}) => {
    return (
        <Box
            margin={"1.5rem 0"}
            sx={{
                padding: "1.5rem",
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                border: `1px solid #e0e0e0`,
                boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
            }}
        >
            <Box display="flex" alignItems="center" gap={1}>
                {IconComponent && (
                    <IconComponent sx={{ color: blueThemeColor }} />
                )}
                <Typography variant="h6" sx={{ color: blueThemeColor }}>
                    {title}
                </Typography>
            </Box>
            <Box display="flex" alignItems="flex-start" gap={1} mt={2}>
                <InfoIcon
                    sx={{ color: "info.main", mt: 0.3 }}
                    fontSize="small"
                />
                <Typography color="text.secondary">{message}</Typography>
            </Box>
            {suggestion && (
                <Box display="flex" alignItems="center" gap={1} mt={2}>
                    <DateRangeIcon
                        sx={{ color: "text.disabled" }}
                        fontSize="small"
                    />
                    <Typography variant="body2" fontStyle="italic">
                        {suggestion}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

const Index = () => {
    const [accounts, setAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [currentData, setCurrentData] = useState(null);
    const [timeRange, setTimeRange] = useState({
        start: new Date(new Date().setDate(new Date().getDate() - 7))
            .toISOString()
            .split("T")[0],
        end: today,
    });
    const [loading, setLoading] = useState(false);
    const [accountsLoading, setAccountsLoading] = useState(true);
    const [dateError, setDateError] = useState("");
    const auth = useAuth(); // Make sure useAuth() provides accessToken
    const storedDeviceId = localStorage.getItem("device-id");

    // Fetch Instagram accounts
    const fetchAccounts = useCallback(async () => {
        setAccountsLoading(true);
        try {
            const response = await fetch(
                API_ENDPOINTS.INSTAGRAM_CHANNEL_ACCOUNTS,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${auth.accessToken}`,
                        "x-cc-device-id": storedDeviceId,
                    },
                }
            );
            const json = await response.json();

            if (json.data && json.data.length > 0) {
                setAccounts(json.data);
                setSelectedAccount(json.data[0]);
            } else {
                console.log("No Instagram accounts found");
                setAccounts([]);
                setSelectedAccount(null);
            }
        } catch (error) {
            console.error("Error fetching Instagram accounts:", error);
            setAccounts([]);
            setSelectedAccount(null);
        } finally {
            setAccountsLoading(false);
        }
    }, [auth.accessToken, storedDeviceId]); // auth.accessToken dependency

    useEffect(() => {
        if (auth.accessToken) {
            // Only fetch if accessToken is available
            fetchAccounts();
        }
    }, [fetchAccounts, auth.accessToken]);

    // Fetch data only if date range is valid and account is selected
    const fetchData = useCallback(async () => {
        if (!selectedAccount || !selectedAccount.user_id) {
            setCurrentData(null);
            return;
        }

        if (new Date(timeRange.end) <= new Date(timeRange.start)) {
            setDateError("The 'To' date must be after the 'From' date.");
            setCurrentData(null);
            return;
        }
        setDateError("");

        setLoading(true);
        try {
            const response = await fetch(
                `${API_ENDPOINTS.INSTAGRAM_CHANNEL_METRICS}?user_id=${selectedAccount.user_id}&since=${timeRange.start}&upto=${timeRange.end}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${auth.accessToken}`,
                        "x-cc-device-id": storedDeviceId,
                    },
                }
            );
            const json = await response.json();

            if (!json.data || json.data.length === 0) {
                setDateError(
                    'No data found for the selected date range. Please try a different "To" date.'
                );
                setCurrentData(null);
            } else {
                setCurrentData(json.data);
                setDateError("");
            }
        } catch (error) {
            console.error("Error Retrieving Data:", error);
            setCurrentData(null);
            setDateError("Failed to retrieve data. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [timeRange, auth.accessToken, storedDeviceId, selectedAccount]);

    useEffect(() => {
        if (selectedAccount && selectedAccount.user_id && auth.accessToken) {
            // Also check for accessToken
            fetchData();
        } else {
            setCurrentData(null);
            setLoading(false);
        }
    }, [fetchData, selectedAccount, auth.accessToken]);

    const insights = useMemo(() => {
        if (!currentData || !Array.isArray(currentData)) return {};
        return currentData.reduce((acc, item) => {
            acc[item.metric] = item;
            return acc;
        }, {});
    }, [currentData]);

    const viewsData = useMemo(() => {
        if (
            !currentData ||
            !insights["follower_views"] ||
            !insights["media_views"]
        )
            return null;
        return {
            followers: insights["follower_views"],
            content: insights["media_views"],
        };
    }, [insights, currentData]);

    const interactionsData = useMemo(
        () => insights["interactions"] || null,
        [insights]
    );

    const followersData = useMemo(() => {
        if (insights["online_followers"] && insights["followers_count"]) {
            return {
                total_followers:
                    insights["followers_count"]?.raw_data?.followers_count,
                ...insights["online_followers"],
            };
        }
        return null;
    }, [insights]);

    const genderData = useMemo(
        () => insights["followers_gender_demographics"] || null,
        [insights]
    );
    const ageData = useMemo(
        () => insights["followers_age_demographics"] || null,
        [insights]
    );
    const cityData = useMemo(
        () => insights["followers_city_demographics"] || null,
        [insights]
    );

    const handleDateChange = (field, value) => {
        setTimeRange((prev) => ({ ...prev, [field]: value }));
        setDateError("");
    };

    const handleSelectChange = (e) => {
        setSelectedAccount(e.target.value);
        setDateError("");
        setCurrentData(null);
    };

    const commonSuggestion =
        "Try selecting a wider date range for more comprehensive analytics.";

    return (
        <Container>
            <Grid2
                container
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
                marginBottom="2rem"
            >
                <Grid2 item>
                    <Typography variant="h6">Account Insights</Typography>
                </Grid2>
                <Grid2 item>
                    {selectedAccount && (
                        <Box display="flex" gap="1rem">
                            <Box>
                                <InputLabel
                                    id="start-date-label"
                                    sx={{ fontSize: "0.8rem" }}
                                >
                                    From
                                </InputLabel>
                                <Input
                                    type="date"
                                    id="start-date-label"
                                    value={timeRange.start}
                                    sx={dateInputStyle}
                                    inputProps={{ max: today }}
                                    onChange={(e) =>
                                        handleDateChange(
                                            "start",
                                            e.target.value
                                        )
                                    }
                                />
                            </Box>
                            <Box>
                                <InputLabel
                                    id="end-date-label"
                                    sx={{ fontSize: "0.8rem" }}
                                >
                                    To
                                </InputLabel>
                                <Input
                                    type="date"
                                    id="end-date-label"
                                    value={timeRange.end}
                                    sx={dateInputStyle}
                                    inputProps={{ max: today }}
                                    onChange={(e) =>
                                        handleDateChange("end", e.target.value)
                                    }
                                    error={!!dateError}
                                />
                            </Box>
                        </Box>
                    )}
                    {dateError && selectedAccount && (
                        <Typography
                            color="error"
                            variant="caption"
                            sx={{ display: "block", mt: 0.5 }}
                        >
                            {dateError}
                        </Typography>
                    )}
                </Grid2>
            </Grid2>

            {accountsLoading ? (
                <Box display="flex" justifyContent="center" my={4}>
                    <CircularProgress />
                    <Typography ml={2}>Loading accounts...</Typography>
                </Box>
            ) : (
                <>
                    {accounts.length > 0 ? (
                        <Box
                            width="100%"
                            display="flex"
                            justifyContent="flex-start"
                            gap="1rem"
                            mb={3}
                        >
                            <FormControl fullWidth sx={{ maxWidth: 400 }}>
                                <InputLabel id="instagram-select-label">
                                    Select Instagram Account
                                </InputLabel>
                                <Select
                                    labelId="instagram-select-label"
                                    id="instagram-account-select"
                                    value={selectedAccount || ""}
                                    label="Select Instagram Account"
                                    onChange={handleSelectChange}
                                >
                                    {accounts.map((account) => (
                                        <MenuItem
                                            key={account.user_id}
                                            value={account}
                                        >
                                            {account.username}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    ) : (
                        !loading && (
                            <Box display="flex" justifyContent="center" my={4}>
                                <Typography>
                                    No Instagram accounts found.
                                </Typography>
                            </Box>
                        )
                    )}

                    {loading ? (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            height="40vh"
                        >
                            <CircularProgress />
                            <Typography ml={2}>Loading insights...</Typography>
                        </Box>
                    ) : (
                        <>
                            {selectedAccount && !dateError && (
                                <>
                                    {currentData ? (
                                        <>
                                            <Typography
                                                variant="h5"
                                                margin={"1rem 0 2rem 0"}
                                                textTransform={"capitalize"}
                                                textAlign="center"
                                            >
                                                {selectedAccount.username}
                                            </Typography>

                                            {viewsData ? (
                                                <Views data={viewsData} />
                                            ) : (
                                                <DataUnavailableMessage
                                                    icon={VisibilityOffIcon}
                                                    title="Views"
                                                    message="View data is unavailable for the selected date range. Please increase the date range to see follower and content type view statistics."
                                                    suggestion={
                                                        commonSuggestion
                                                    }
                                                />
                                            )}

                                            {interactionsData ? (
                                                <Interactions
                                                    data={interactionsData}
                                                />
                                            ) : (
                                                <DataUnavailableMessage
                                                    icon={WarningAmberIcon}
                                                    title="Interactions"
                                                    message="Interaction data is unavailable for the selected date range. Please increase the date range to see engagement statistics by content type."
                                                    suggestion={
                                                        commonSuggestion
                                                    }
                                                />
                                            )}

                                            {followersData ? (
                                                <Followers
                                                    data={followersData}
                                                />
                                            ) : (
                                                <DataUnavailableMessage
                                                    icon={WarningAmberIcon} // Consider PeopleIcon if imported
                                                    title="Followers"
                                                    message="Follower data is unavailable for the selected date range. Please increase the date range to see total followers and online follower statistics."
                                                    suggestion={
                                                        commonSuggestion
                                                    }
                                                />
                                            )}

                                            {genderData ? (
                                                <GenderDemographics
                                                    data={genderData}
                                                />
                                            ) : (
                                                <DataUnavailableMessage
                                                    icon={WarningAmberIcon} // Consider WcIcon if imported
                                                    title="Gender Demographics"
                                                    message="Gender distribution data is unavailable for the selected date range. Please increase the date range to see the gender distribution chart."
                                                    suggestion={
                                                        commonSuggestion
                                                    }
                                                />
                                            )}

                                            {ageData ? (
                                                <AgeDemographics
                                                    data={ageData}
                                                />
                                            ) : (
                                                <DataUnavailableMessage
                                                    icon={WarningAmberIcon} // Consider CakeIcon if imported
                                                    title="Age Demographics"
                                                    message="Age distribution data is unavailable for the selected date range. Please increase the date range to see the age distribution chart."
                                                    suggestion={
                                                        commonSuggestion
                                                    }
                                                />
                                            )}

                                            {cityData ? (
                                                <CityDemographics
                                                    data={cityData}
                                                />
                                            ) : (
                                                <DataUnavailableMessage
                                                    icon={WarningAmberIcon} // Consider LocationCityIcon if imported
                                                    title="City Demographics"
                                                    message="City distribution data is unavailable for the selected date range. Please increase the date range to see the geographical distribution of your audience."
                                                    suggestion={
                                                        commonSuggestion
                                                    }
                                                />
                                            )}
                                        </>
                                    ) : (
                                        selectedAccount &&
                                        !dateError && ( // If currentData is null but no specific API error for date range
                                            <Box
                                                display="flex"
                                                justifyContent="center"
                                                my={4}
                                            >
                                                <Typography>
                                                    No insights data to display
                                                    for{" "}
                                                    {selectedAccount.username}{" "}
                                                    for the selected period.
                                                </Typography>
                                            </Box>
                                        )
                                    )}
                                </>
                            )}
                        </>
                    )}
                </>
            )}
        </Container>
    );
};

export default Index;
